/**
 * "A new version is ready."
 *
 * The service worker is registered with `registerType: 'prompt'`, which is
 * right — swapping the app under an engineer halfway through a breaker is how
 * you lose a lineup's readings. But prompt only works if something actually
 * prompts. Without this, a deployed fix sits in a waiting worker for ever and
 * every phone keeps running the old build. That is exactly what happened on the
 * first deploy: the fix was live on the host and invisible in the browser.
 *
 * So the rule is: tell them, let them choose, and be honest about the cost.
 * Reloading keeps everything already queued — readings and photos are in
 * IndexedDB the moment they are taken — but it does clear the breaker page the
 * engineer is looking at, so the honest advice is to finish the breaker first.
 */

import { memo } from 'react';

export interface UpdateBannerProps {
  /** True once a new build is downloaded and waiting to take over. */
  ready: boolean;
  onReload: () => void;
  onDismiss?: (() => void) | undefined;
  /** Readings and photos waiting to upload, so the wording can be accurate. */
  pendingWrites?: number | undefined;
}

export const UpdateBanner = memo(function UpdateBanner({
  ready, onReload, onDismiss, pendingWrites = 0,
}: UpdateBannerProps) {
  if (!ready) return null;

  return (
    <div className="update-banner" role="status" data-testid="update-banner">
      <p>
        <strong>A new version is ready.</strong>{' '}
        {pendingWrites > 0
          ? `Your ${pendingWrites} queued ${pendingWrites === 1 ? 'item is' : 'items are'} saved and will survive the reload — but the breaker on screen will clear, so finish it first.`
          : 'The breaker on screen will clear, so finish it first.'}
      </p>
      <div className="update-actions">
        <button type="button" onClick={onReload}>
          Reload now
        </button>
        {onDismiss ? (
          <button type="button" className="secondary" onClick={onDismiss}>
            After this breaker
          </button>
        ) : null}
      </div>
    </div>
  );
});
